import React, {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
} from 'react';
import clsx from 'clsx';
import omit from 'lodash/omit';
import Checkbox, {
  CheckboxProps,
  CheckboxClasses,
} from '@mui/material/Checkbox';
import UncheckedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckedIcon from '@mui/icons-material/CheckBoxRounded';
import Button from 'pages/Dashboard/components/Button';
import isNil from 'lodash/isNil';

const iconClassName = '!w-5 !h-5 !text-inherit';
const buttonClassName = 'pl-3 h-8 flex gap-2';

type Props = Omit<CheckboxProps, 'classes'> & {
  label?: string;
  classes?: Partial<CheckboxClasses & { button?: string }>;
  icon?: ReactNode;
  checkedIcon?: ReactNode;
};

export default function ButtonCheckbox({
  label = '',
  value,
  checked,
  classes,
  icon,
  checkedIcon,
  disabled = false,
  onChange,
  ...props
}: Props) {
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === ' ') {
      event.preventDefault();
      const syntheticEvent = {
        target: { checked: !checked },
      } as ChangeEvent<HTMLInputElement>;
      onChange?.(syntheticEvent, !checked);
    }
  };

  return (
    <Checkbox
      {...props}
      tabIndex={-1}
      value={value}
      classes={omit(classes, 'button')}
      checked={checked}
      disabled={disabled}
      disableRipple
      onChange={onChange}
      icon={(
        <Button
          variant="outlined"
          className={clsx(buttonClassName, '!text-gray-500', classes?.button)}
          disabled={disabled}
          onKeyDown={handleKeyDown}
        >
          {isNil(icon) ? <UncheckedIcon className={iconClassName} /> : icon}
          {label}
        </Button>
      )}
      checkedIcon={(
        <Button
          variant="outlined"
          color="primary"
          className={clsx(buttonClassName, '!bg-blue-500 !text-white', classes?.button)}
          disableElevation
          disabled={disabled}
          onKeyDown={handleKeyDown}
        >
          {isNil(checkedIcon) ? <CheckedIcon className={iconClassName} /> : checkedIcon}
          {label}
        </Button>
      )}
    />
  );
}
